/**
 * RTK Query endpoints for the repositories service (commits / pull-requests /
 * sync — epic-6 §5, GIT-2/GIT-4).
 *
 * Base path convention: `/core-proxy/repositories/*` so the admin-ui nginx
 * rewrites `^/api/core-proxy/(.*)$` → `/$1` onto the repositories service
 * (:3700). The generic dev proxy falls through to core for these paths.
 */
import { apiSlice } from './apiSlice';
import type {
  Commit,
  PullRequest,
  PullRequestReview,
  CreatePullRequestDto,
  UpdatePullRequestDto,
  PullRequestReviewEvent,
  CommitSyncResult,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

export interface CommitListResult {
  data: Commit[];
  limit: number;
  offset: number;
}

export const repositoriesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Commits ───────────────────────────────────────────────────────────
    getRepositoryCommits: builder.query<
      CommitListResult,
      { id: string; branch?: string; limit?: number; offset?: number }
    >({
      query: ({ id, branch, limit, offset }) => ({
        url: `/core-proxy/repositories/${id}/commits`,
        params: { ...(branch ? { branch } : {}), ...(limit ? { limit } : {}), ...(offset ? { offset } : {}) },
      }),
      transformResponse: (response: CommitListResult) => response,
      providesTags: (_result, _error, { id }) => [{ type: 'Repository', id: `commits-${id}` }],
    }),

    // Manual git sync (GIT-4) — returns the head-sha watermark update.
    syncRepositoryCommits: builder.mutation<CommitSyncResult, { id: string; branchName?: string }>({
      query: ({ id, branchName }) => ({
        url: `/core-proxy/repositories/${id}/sync`,
        method: 'POST',
        body: branchName ? { branchName } : {},
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Repository', id: `commits-${id}` },
        { type: 'Repository', id: `prs-${id}` },
        { type: 'Repository', id: 'LIST' },
      ],
    }),

    // ── Pull requests (GIT-4) ─────────────────────────────────────────────
    getRepositoryPullRequests: builder.query<
      PullRequest[],
      { id: string; status?: string; state?: string }
    >({
      query: ({ id, status, state }) => ({
        url: `/core-proxy/repositories/${id}/pull-requests`,
        params: { ...(status ? { status } : {}), ...(state ? { state } : {}) },
      }),
      transformResponse: (response: { data: PullRequest[] }) => response.data,
      providesTags: (_result, _error, { id }) => [
        { type: 'Repository', id: `prs-${id}` },
        ...(_result ?? []).map((pr) => ({
          type: 'Repository' as const,
          id: `pr-${id}-${pr.providerNumber}`,
        })),
      ],
    }),

    getPullRequest: builder.query<PullRequest, { id: string; number: number }>({
      query: ({ id, number }) => `/core-proxy/repositories/${id}/pull-requests/${number}`,
      providesTags: (_result, _error, { id, number }) => [
        { type: 'Repository', id: `pr-${id}-${number}` },
      ],
    }),

    createPullRequest: builder.mutation<
      PullRequest,
      { id: string; body: CreatePullRequestDto }
    >({
      query: ({ id, body }) => ({
        url: `/core-proxy/repositories/${id}/pull-requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Repository', id: `prs-${id}` }],
    }),

    updatePullRequest: builder.mutation<
      PullRequest,
      { id: string; number: number; body: UpdatePullRequestDto }
    >({
      query: ({ id, number, body }) => ({
        url: `/core-proxy/repositories/${id}/pull-requests/${number}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id, number }) => [
        { type: 'Repository', id: `pr-${id}-${number}` },
        { type: 'Repository', id: `prs-${id}` },
      ],
    }),

    getPullRequestReviews: builder.query<
      PullRequestReview[],
      { id: string; number: number }
    >({
      query: ({ id, number }) =>
        `/core-proxy/repositories/${id}/pull-requests/${number}/reviews`,
      transformResponse: (response: { data: PullRequestReview[] }) => response.data,
      providesTags: (_result, _error, { id, number }) => [
        { type: 'Repository', id: `pr-reviews-${id}-${number}` },
      ],
    }),

    submitPullRequestReview: builder.mutation<
      PullRequestReview,
      { id: string; number: number; body: string; event?: PullRequestReviewEvent }
    >({
      query: ({ id, number, ...body }) => ({
        url: `/core-proxy/repositories/${id}/pull-requests/${number}/reviews`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id, number }) => [
        { type: 'Repository', id: `pr-reviews-${id}-${number}` },
        { type: 'Repository', id: `pr-${id}-${number}` },
      ],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetRepositoryCommitsQuery,
  useSyncRepositoryCommitsMutation,
  useGetRepositoryPullRequestsQuery,
  useGetPullRequestQuery,
  useCreatePullRequestMutation,
  useUpdatePullRequestMutation,
  useGetPullRequestReviewsQuery,
  useSubmitPullRequestReviewMutation,
} = repositoriesApi;